package org.example.zone360.pages.coachDetails.sessionAllDetails.customHighlight

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.onMouseEnter
import com.varabyte.kobweb.compose.ui.modifiers.onMouseLeave
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.silk.components.icons.fa.FaPause
import com.varabyte.kobweb.silk.components.icons.fa.FaPlay
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import kotlinx.browser.document
import org.example.zone360.components.VideoProgressSliderCustom
import org.example.zone360.models.GetSentencesData
import org.example.zone360.models.GetSessionsData
import org.example.zone360.pages.coachDetails.sessionAllDetails.SentenceAudioWaveform
import org.example.zone360.pages.players_Details.videosSection.encodeFileName
import org.example.zone360.theme.Palette
import org.example.zone360.theme.SitePalette
import org.example.zone360.utils.Res
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Audio
import org.jetbrains.compose.web.dom.Video
import org.w3c.dom.HTMLAudioElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLVideoElement
import kotlin.math.ceil
import kotlin.math.floor

fun validateStartAndEndTime(
    startMinState: MutableState<String>,
    startSecState: MutableState<String>,
    endMinState: MutableState<String>,
    endSecState: MutableState<String>,
    sessionDuration: Double,
    isStartEdited: Boolean = false
) {
    val sessionTotalSeconds = floor(sessionDuration).toInt()

    val startMin = startMinState.value.toIntOrNull() ?: 0
    var startSec = startSecState.value.toIntOrNull() ?: 0
    val endMin = endMinState.value.toIntOrNull() ?: 0
    var endSec = endSecState.value.toIntOrNull() ?: 0

    // Clamp 0–59s
    startSec = startSec.coerceIn(0, 59)
    endSec = endSec.coerceIn(0, 59)

    var startTotal = (startMin * 60) + startSec
    var endTotal = (endMin * 60) + endSec

    if (startTotal >= sessionTotalSeconds) {
        startTotal = 0
    }

    if (isStartEdited) {
        // ✅ Always auto-set end = start + 15s when user changes minutes/seconds
        endTotal = (startTotal + 15).coerceAtMost(sessionTotalSeconds)
    } else {
        if (endTotal > sessionTotalSeconds) {
            endTotal = sessionTotalSeconds
        }
        if (endTotal <= startTotal) {
            endTotal = (startTotal + 15).coerceAtMost(sessionTotalSeconds)
        }
    }

    // Update UI state
    startMinState.value = (startTotal / 60).toString()
    startSecState.value = (startTotal % 60).toString()
    endMinState.value = (endTotal / 60).toString()
    endSecState.value = (endTotal % 60).toString()

    (document.getElementById(Res.Id.startMinute) as? HTMLInputElement)?.value = startMinState.value
    (document.getElementById(Res.Id.startSeconds) as? HTMLInputElement)?.value = startSecState.value
    (document.getElementById(Res.Id.endMinute) as? HTMLInputElement)?.value = endMinState.value
    (document.getElementById(Res.Id.endSeconds) as? HTMLInputElement)?.value = endSecState.value
}



@Composable
fun HighlightSegmentPreview(
    sessionId: String,
    sitePalette: SitePalette,
    sessionData: GetSessionsData,
    startMinute: Int,
    startSecond: Int,
    endMinute: Int,
    endSecond: Int,
    breakpoint: Breakpoint,
    originalAudio: Boolean
) {
    var isBuffering by remember { mutableStateOf(false) }

    val startTime = (startMinute * 60 + startSecond).toDouble()
    val endTime = (endMinute * 60 + endSecond).toDouble()
    val videoAvailable = sessionData.videoFile.isNotEmpty()

    val videoSrc = "${Res.Video.base_Video_URL}${encodeFileName(sessionData.videoFile)}"
    val audioSrc = "${Res.Audio.audio_base_url}${sessionData.sessionId}"

    var progress by remember { mutableStateOf(0f) }
    var isHovered by remember { mutableStateOf(false) }

    val currentVideoRef = remember { mutableStateOf<HTMLVideoElement?>(null) }
    val currentAudioRef = remember { mutableStateOf<HTMLAudioElement?>(null) }

    var isPlaying by remember { mutableStateOf(false) }

    DisposableEffect(Unit) {
        onDispose {
            currentVideoRef.value?.pause()
            currentAudioRef.value?.pause()
            isPlaying = false
            progress = 0f
        }
    }
    fun playVideoWithAudio() {
        if (videoAvailable) {
            val videoElement = document.getElementById("highlight_video_$sessionId") as? HTMLVideoElement
            val audioElement = document.getElementById("highlight_audio_$sessionId") as? HTMLAudioElement

            videoElement?.let { video ->
                audioElement?.let { audio ->
                    video.currentTime = startTime
                    video.muted = !originalAudio

                    if (!originalAudio) {
                        audio.currentTime = startTime
                        audio.pause()
                    }

                    currentVideoRef.value = video
                    if (!originalAudio) {
                        currentAudioRef.value = audio
                    }

                    isBuffering = true
                    // Play video when ready
                    video.oncanplay = {
                        video.play().then {
                            isBuffering = false
                            isPlaying = true
                        }
                    }

                    // Sync: Play audio only when video is truly playing
                    video.onplaying = {
                        isBuffering = false
                        if (!originalAudio) {
                            audio.play()
                        }
                    }

                    // Pause audio when video buffers
                    video.onwaiting = {
                        isBuffering = true
                        if (!originalAudio) {
                            audio.pause()
                        }
                    }


                    // Main sync + end logic
                    video.ontimeupdate = {
                        if (video.currentTime >= endTime) {
                            video.pause()
                            if (!originalAudio) {
                                audio.pause()
                            }
                            isPlaying = false
                            progress = 0f
                        } else {
                            progress = ((video.currentTime - startTime) / (endTime - startTime)).toFloat()
                        }
                    }

                }
            }
        } else {
            // Fallback to audio-only playback
            val audioElement = document.getElementById("highlight_audio_$sessionId") as? HTMLAudioElement

            audioElement?.let { audio ->
                audio.currentTime = startTime
                audio.play()

                currentAudioRef.value = audio
                isPlaying = true

                audio.ontimeupdate = {
                    if (audio.currentTime >= endTime) {
                        audio.pause()
                        isPlaying = false
                        progress = 0f
                    } else {
                        progress = ((audio.currentTime - startTime) / (endTime - startTime)).toFloat()
                    }
                }
            }
        }
    }


    if (videoAvailable) {
        Box(
            modifier = Modifier.fillMaxWidth()
                .margin(topBottom = 10.px)
                .onMouseEnter { isHovered = true }
                .onMouseLeave { isHovered = false },
            contentAlignment = Alignment.Center
        ) {
            // Hidden audio element for sync
            Audio(attrs = {
                attr("id", "highlight_audio_$sessionId")
                attr("src", audioSrc)
                attr("preload", "auto")
                attr("hidden", "true")
            })

            if (!isPlaying) {
                Box(
                    modifier = Modifier.size(40.px)
                        .zIndex(9999)
                        .borderRadius(100.px)
                        .backgroundColor(if (startTime == 0.0 &&  endTime == 0.0) Palette.WaveBarColor.rgbLight else sitePalette.primary.rgb)
                        .cursor(Cursor.Pointer)
                        .justifyContent(JustifyContent.Center)
                        .onClick {
                            if (startTime == 0.0 &&  endTime == 0.0)
                                return@onClick
                            playVideoWithAudio() },
                    contentAlignment = Alignment.Center
                ) {
                    FaPlay(size = IconSize.SM, modifier = Modifier.color(Colors.White)
                        .margin(left = 2.px, top = 1.px))
                }
            } else if (isHovered) {
                Box(
                    modifier = Modifier.size(40.px)
                        .zIndex(9999)
                        .borderRadius(100.percent)
                        .backgroundColor(if (startTime == 0.0 &&  endTime == 0.0) Palette.WaveBarColor.rgbLight else sitePalette.primary.rgb)
                        .cursor(Cursor.Pointer)
                        .onClick {
                            currentVideoRef.value?.pause()
                            currentAudioRef.value?.pause()
                            isPlaying = false
                            progress = 0f
                        },
                    contentAlignment = Alignment.Center
                ) {
                    FaPause(size = IconSize.SM, modifier = Modifier.color(Colors.White)
                        .margin(top = 1.px, left = 2.px))
                }
            }

            if (isHovered && isPlaying) {
                VideoProgressSliderCustom(
                    modifier = Modifier.fillMaxWidth(97.percent)
                        .align(Alignment.BottomCenter)
                        .margin(bottom = 20.px)
                        .zIndex(9999),
                    progress = progress,
                    onProgressChange = { newProgress ->
                        val newSeekTime = startTime + (endTime - startTime) * newProgress
                        currentVideoRef.value?.currentTime = newSeekTime.coerceIn(startTime, endTime)
                        currentAudioRef.value?.currentTime = newSeekTime.coerceIn(startTime, endTime)
                        progress = newProgress
                    }
                )
            }

            Video(
                attrs = {
                    attr("id", "highlight_video_$sessionId")
                    attr("src", videoSrc)
                    attr("muted", "true")
                    attr("preload", "auto")
                    style {
                        property("width", "100%")
                        property("height", "100%")
                        property("border-radius", "8px")
                        property("object-fit", "cover")
                    }
                }
            )
        }
    } else {
        Row(
            modifier = Modifier.width(if (breakpoint >= Breakpoint.LG) 390.px else 100.percent)
                .height(45.px)
                .borderRadius(8.px)
                .padding(8.px)
                .gap(8.px)
                .backgroundColor(sitePalette.searchBackground.rgb),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier.width(30.px)
                    .height(28.px)
                    .borderRadius(100.percent)
                    .backgroundColor(if (startTime == 0.0 &&  endTime == 0.0) Palette.WaveBarColor.rgbLight else sitePalette.primary.rgb)
                    .cursor(Cursor.Pointer)
                    .onClick {
                        if (startTime == 0.0 &&  endTime == 0.0)
                            return@onClick
                        if (!isPlaying){playVideoWithAudio()}
                    else{
                        isPlaying  = false
                        currentAudioRef.value?.pause()
                        progress = 0f

                    }},
                contentAlignment = Alignment.Center
            ) {
                if (!isPlaying) {
                    FaPlay(size = IconSize.XXS, modifier = Modifier.color(Colors.White)
                        .margin(top = 1.px, left = 1.px))
                } else {
                    FaPause(size = IconSize.XXS, modifier = Modifier.color(Colors.White)
                        .margin(top = 1.px, left = 2.px))
                }
            }

            SentenceAudioWaveform(
                modifier = Modifier.fillMaxWidth(),
                progress = progress,
                startTime = startTime,
                endTime = endTime,
                isPlaying = isPlaying,
                onProgressChange = { newProgress ->
                    if (startTime == 0.0 &&  endTime == 0.0) return@SentenceAudioWaveform
                    val newSeekTime = startTime + (endTime - startTime) * newProgress
                    currentAudioRef.value?.currentTime = newSeekTime.coerceIn(startTime, endTime)
                    progress = newProgress
                }
            )
        }

        // Hidden audio element
        Audio(attrs = {
            attr("id", "highlight_audio_$sessionId")
            attr("src", audioSrc)
            attr("preload", "auto")
            attr("hidden", "true")
        })
    }
}



fun getTranscriptFromSentences(
    sentences: List<GetSentencesData>,
    startTime: Double,
    endTime: Double,
    keywordFilter: String? = null
): String {
    val unique = mutableListOf<GetSentencesData>()

    sentences
        .filter { it.endTime > startTime && it.startTime < endTime }
        .sortedByDescending { it.endTime - it.startTime }
        .forEach { current ->
            // Check if this overlaps with any already added sentence
            val overlaps = unique.any {
                !(current.endTime <= it.startTime || current.startTime >= it.endTime)
            }
            if (!overlaps) unique.add(current)
        }

    val result = unique.sortedBy { it.startTime }.mapNotNull { sentence ->
        val duration = sentence.endTime - sentence.startTime
        if (duration <= 0 || sentence.transcript.isBlank()) return@mapNotNull null

        val words = sentence.transcript.trim().split(Regex("\\s+"))
        val wordsPerSecond = words.size / duration

        val relStart = (startTime - sentence.startTime).coerceAtLeast(0.0)
        val relEnd = (endTime - sentence.startTime).coerceAtMost(duration)

        val iStart = (relStart * wordsPerSecond).toInt().coerceIn(0, words.size)
        val iEnd = ceil(relEnd * wordsPerSecond).toInt().coerceIn(0, words.size)

        if (iStart < iEnd) words.subList(iStart, iEnd).joinToString(" ") else null
    }

    return result.joinToString(" ").trim()
}



fun isDuplicateHighlightByTime(
    keyword: String,
    newStart: Double,
    newEnd: Double,
    sentences: List<GetSentencesData>
): Boolean {
    val normalizedKeyword = keyword.trim().lowercase()

    return sentences.any { sentence ->
        val existingKeywords = sentence.keywords ?: emptyList()

        existingKeywords.any { existingKeyword ->
            existingKeyword.text.trim().lowercase() == normalizedKeyword &&
                    newStart >= sentence.startTime &&
                    newEnd <= sentence.endTime
        }
    }
}
